import DebounceSearchInput from "@/components/DebounceSearchInput/DebounceSearchInput";
import Button from "@mui/joy/Button";
import Stack from "@mui/joy/Stack";
import Typography from "@mui/joy/Typography";
import InvoicesList from "./InvoicesList";
import { HEADER_HEIGHT, NAVBAR_HEIGHT } from "@/types/constants/common";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getFilteredInvoices, getInvoices } from "@/services/api/interface-validator/invoices";
import NotFoundFallback from "@/components/NotFoundFallback/NotFoundFallback";
import { IFInvoiceWithDetailsObj } from "@/types/interfaces/crm";
import InvoicesSort, { SortField } from "./InvoicesSort";
import Loader from "@/components/Loader/Loader";
import ErrorFallback from "@/components/ErrorFallback/ErrorFallback";
import RefreshIcon from "@mui/icons-material/Refresh";
import DashboardIcon from "@mui/icons-material/Dashboard";
import InvoicesSummaryByDate from "./InvoicesSummaryByDate";
import { useNavigate } from "react-router-dom";
import { ADMIN_ROUTE_PREFIX } from "@/types/constants/common";
import Tooltip from "@mui/joy/Tooltip";
import { useSearchParams } from "react-router-dom";
import { ProductCodesMap } from "@/types/constants/productCodes";
import { toast } from "sonner";
import Tab from "@mui/joy/Tab";
import TabList from "@mui/joy/TabList";
import Tabs from "@mui/joy/Tabs";
import { INTERFACE_TEAM_MEMBERS } from "@/types/constants/interfaceTeamMembers";
import { getInterfaceInvoices } from "@/services/api/crm/financeQa";
import useAuthStore from "@/zustand/authStore";
import MultiSelectFilterBox from "@/components/FilterBoxes/MultiSelectFilterBox";
import { formatInTorontoTime } from "../../utility/utils";

export const INVOICES_SIDEBAR_WIDTH = 400;

const productCodeOptions = [
  { value: "", label: "All" },
  ...Object.entries(ProductCodesMap).map(([code, name]) => ({ value: code, label: `${code} - ${name}` })),
];

const tabs = {
  unposted: {
    id: "unposted",
    accessName: (count: number) => `Pending (${count})`,
  },
  posted: {
    id: "posted",
    accessName: (count: number) => `Ready (${count})`,
  },
  parked: {
    id: "parked",
    accessName: (count: number) => `Parked (${count})`,
    color: "warning",
  },
};

interface Props {
  selectedInvoice: string;
  setSelectedInvoiceIndex: (index: number) => void;
  selectedInvoiceIndex: number;
  invoices: IFInvoiceWithDetailsObj[];
  setInvoices: (invoices: IFInvoiceWithDetailsObj[]) => void;
}

export default function InvoicesSidebar({
  invoices,
  setInvoices,
  selectedInvoice,
  setSelectedInvoiceIndex,
  selectedInvoiceIndex,
}: Props) {
  const user = useAuthStore((state) => state.user);
  const [searchText, setSearchText] = useState("");
  const [sortedField, setSortedField] = useState<SortField>("dateAscending");
  const [productCodes, setProductCodes] = useState<string[]>([]);
  const [assignees, setAssignees] = useState<string[]>([]);
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<string>(tabs.unposted.id);
  const [tabCounts, setTabCounts] = useState<Record<string, number>>({});
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const invoicesQuery = useQuery({
    queryKey: ["interfaceInvoices"],
    queryFn: getInvoices,
  });

  const filteredSortedAssignees = useMemo(() => {
    if (
      assignees.length === 0 ||
      assignees.length === INTERFACE_TEAM_MEMBERS.length + 1 || //when everything is selected
      (assignees.length === 1 && assignees.includes("")) || // when only "All" is selected
      (assignees.length === INTERFACE_TEAM_MEMBERS.length && !assignees.includes("")) // when everything is selected but not "All"
    )
      return [];

    const filteredAssignees = assignees.filter((assignee) => assignee !== "").sort();
    return filteredAssignees;
  }, [assignees]);

  const crmInterfaceInvoicesQuery = useQuery({
    queryKey: ["crmInterfaceInvoices", { assignees: filteredSortedAssignees }],
    queryFn: () => getInterfaceInvoices({ assignees: filteredSortedAssignees }),
    enabled: filteredSortedAssignees.length > 0, // Only fetch when there are specific assignees selected
  });

  const allInvoiceIds = useMemo(
    () => (invoicesQuery.data?.invoices || []).map((invoice) => invoice.invoiceId),
    [invoicesQuery.data?.invoices],
  );

  const parkedInvoicesQuery = useQuery({
    queryKey: ["crmParkedInterfaceInvoices", allInvoiceIds],
    queryFn: () => getInterfaceInvoices({ clInvoiceIds: allInvoiceIds }),
    enabled: allInvoiceIds.length > 0,
  });

  const parkedIdSet = useMemo(() => {
    const set = new Set<number>();
    (parkedInvoicesQuery.data?.invoices || []).forEach((invoice) => {
      if (invoice.status === "parked") set.add(invoice.clInvoiceId);
    });
    return set;
  }, [parkedInvoicesQuery.data?.invoices]);

  const filterByProductCodeInvoices = useCallback(
    async (invoices: IFInvoiceWithDetailsObj[], productCodes?: string[]) => {
      if (
        !productCodes ||
        productCodes.length === 0 ||
        productCodes.length === productCodeOptions.length || //when everything is selected
        (productCodes.length === 1 && productCodes[0] === "") ||
        (productCodes.length === productCodeOptions.length - 1 && !productCodes.includes("")) //when everything selected but not "All"
      ) {
        return invoices;
      }

      try {
        const filteredProductCodes = productCodes.filter((code) => code !== "");
        const result = await queryClient.ensureQueryData({
          queryKey: ["filteredInvoices", filteredProductCodes],
          queryFn: () => getFilteredInvoices(filteredProductCodes),
        });
        const resultSet = new Set();
        result.invoices.forEach((invoice) => {
          resultSet.add(invoice.invoiceId);
        });

        return invoices.filter((invoice) => resultSet.has(invoice.invoiceId));
      } catch (error) {
        toast.error("Error while fetching invoices by product code");
        setProductCodes([]);
        return invoices;
      }
    },
    [queryClient],
  );

  const filterByDates = useCallback((invoices: IFInvoiceWithDetailsObj[], selectedDates: string[]) => {
    if (selectedDates.length === 0) return invoices;

    return invoices.filter((invoice) => {
      if (invoice.createdTime) {
        const invoiceDateStr = formatInTorontoTime(new Date(invoice.createdTime), "PPP zzz");
        return selectedDates.includes(invoiceDateStr);
      }
      return false;
    });
  }, []);

  const filterInvoices = useCallback((invoices: IFInvoiceWithDetailsObj[], searchText: string) => {
    if (searchText) {
      return (
        invoices?.filter((invoice: IFInvoiceWithDetailsObj) => {
          const lowerSearchText = searchText.toLowerCase();

          return (
            (invoice.profileName?.toLowerCase() || "").includes(lowerSearchText) ||
            (invoice.invoiceNumber?.toLowerCase() || "").includes(lowerSearchText)
          );
        }) || []
      );
    }
    return invoices || [];
  }, []);

  const sortInvoices = useCallback((invoices: IFInvoiceWithDetailsObj[], sortedField: SortField) => {
    if (sortedField) {
      if (sortedField == "dateAscending") {
        return [...invoices].sort((a, b) => {
          const aValue = new Date(a?.createdTime || 0);
          const bValue = new Date(b?.createdTime || 0);
          return aValue.getTime() - bValue.getTime();
        });
      }

      if (sortedField == "dateDescending") {
        return [...invoices].sort((a, b) => {
          const aValue = new Date(a?.createdTime || 0);
          const bValue = new Date(b?.createdTime || 0);
          return bValue.getTime() - aValue.getTime();
        });
      }

      if (sortedField == "invoiceNumberAscending") {
        return [...invoices].sort((a, b) => {
          const aValue = parseFloat(a?.invoiceNumber || "0");
          const bValue = parseFloat(b?.invoiceNumber || "0");
          return aValue - bValue;
        });
      }

      if (sortedField == "invoiceNumberDescending") {
        return [...invoices].sort((a, b) => {
          const aValue = parseFloat(a?.invoiceNumber || "0");
          const bValue = parseFloat(b?.invoiceNumber || "0");
          return bValue - aValue;
        });
      }

      if (sortedField == "recordNumberAscending") {
        return [...invoices].sort((a, b) => {
          const aValue = a?.record || 0;
          const bValue = b?.record || 0;
          return aValue - bValue;
        });
      }

      if (sortedField == "recordNumberDescending") {
        return [...invoices].sort((a, b) => {
          const aValue = a?.record || 0;
          const bValue = b?.record || 0;
          return bValue - aValue;
        });
      }
      return invoices;
    }
    return invoices;
  }, []);

  const partitionByStatus = useCallback(
    (invoices: IFInvoiceWithDetailsObj[]) => {
      const posted: IFInvoiceWithDetailsObj[] = [];
      const unposted: IFInvoiceWithDetailsObj[] = [];
      const parked: IFInvoiceWithDetailsObj[] = [];
      invoices.forEach((invoice) => {
        // Parked takes precedence so parked invoices never leak into Pending/Ready.
        if (parkedIdSet.has(invoice.invoiceId)) {
          parked.push(invoice);
        } else if (invoice.postNow) {
          posted.push(invoice);
        } else {
          unposted.push(invoice);
        }
      });
      return { posted, unposted, parked };
    },
    [parkedIdSet],
  );

  const filterByAssignee = useCallback(
    async (invoices: IFInvoiceWithDetailsObj[]) => {
      if (
        assignees.length === 0 ||
        assignees.length === INTERFACE_TEAM_MEMBERS.length + 1 || //when everything is selected
        (assignees.length === 1 && assignees.includes("")) || // when only "All" is selected
        (assignees.length === INTERFACE_TEAM_MEMBERS.length && !assignees.includes("")) // when everything is selected but not "All"
      )
        return invoices;

      try {
        const crmInterfaceInvoicesResult = crmInterfaceInvoicesQuery.data?.invoices || [];
        const resultSet = new Set();
        crmInterfaceInvoicesResult.forEach((invoice) => {
          resultSet.add(invoice.clInvoiceId);
        });
        return invoices.filter((invoice) => resultSet.has(invoice.invoiceId));
      } catch (error) {
        toast.error("Error while fetching invoices by assignee");
        setAssignees([]);
        return invoices;
      }
    },
    [crmInterfaceInvoicesQuery.data?.invoices, assignees],
  );

  useEffect(() => {
    async function applyFilters() {
      setIsLoading(true);
      const filteredByProductCodeInvoices = await filterByProductCodeInvoices(
        invoicesQuery?.data?.invoices || [],
        productCodes,
      );
      const filteredInvoices = filterInvoices(filteredByProductCodeInvoices, searchText);
      const sortedInvoices = sortInvoices(filteredInvoices, sortedField);
      const dateFilteredInvoices = filterByDates(sortedInvoices, selectedDates);
      const assigneeFilteredInvoices = await filterByAssignee(dateFilteredInvoices);
      const { posted, unposted, parked } = partitionByStatus(assigneeFilteredInvoices);
      setTabCounts({
        [tabs.unposted.id]: unposted.length,
        [tabs.posted.id]: posted.length,
        [tabs.parked.id]: parked.length,
      });
      setInvoices(activeTab === tabs.posted.id ? posted : activeTab === tabs.parked.id ? parked : unposted);
      setIsLoading(false);
    }
    applyFilters();
  }, [
    invoicesQuery?.data?.invoices,
    searchText,
    sortedField,
    activeTab,
    productCodes,
    selectedDates,
    assignees,
    filterByAssignee,
    partitionByStatus,
    filterByProductCodeInvoices,
    filterInvoices,
    sortInvoices,
    filterByDates,
  ]);

  useEffect(() => {
    if (user && user.ttandEmail) {
      const member = INTERFACE_TEAM_MEMBERS.find((member) => member.email === user.ttandEmail);
      setAssignees(member ? [member.email] : []);
    }
  }, [user]);

  useEffect(() => {
    if (selectedInvoiceIndex === -1 && searchParams.get("invoiceId") && invoices.length > 0) {
      const index = invoices.findIndex((invoice) => invoice.invoiceId?.toString() === searchParams.get("invoiceId"));
      setSelectedInvoiceIndex(index);
    }
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") {
        if (selectedInvoiceIndex < invoices.length - 1) {
          setSelectedInvoiceIndex(selectedInvoiceIndex + 1);
          setSearchParams({ invoiceId: invoices[selectedInvoiceIndex + 1].invoiceId?.toString() });
        }
      }
      if (e.key === "ArrowLeft") {
        if (selectedInvoiceIndex > 0) {
          setSelectedInvoiceIndex(selectedInvoiceIndex - 1);
          setSearchParams({ invoiceId: invoices[selectedInvoiceIndex - 1].invoiceId?.toString() });
        }
      }
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [setSelectedInvoiceIndex, selectedInvoiceIndex, invoices, setSearchParams]);

  const invoiceDistinctDates = useMemo(() => {
    const invoices = invoicesQuery?.data?.invoices || [];
    const datesSet = new Set<string>();
    invoices.forEach((invoice) => {
      if (invoice.createdTime) {
        const dateStr = formatInTorontoTime(new Date(invoice.createdTime), "PPP zzz");
        datesSet.add(dateStr);
      }
    });
    return Array.from(datesSet);
  }, [invoicesQuery?.data?.invoices]);

  return (
    <Stack
      gap={1}
      sx={{
        left: 0,
        position: "fixed",
        width: `${INVOICES_SIDEBAR_WIDTH}px`,
        bgcolor: "#F6F8FA",
        flexGrow: 1,
        top: HEADER_HEIGHT + NAVBAR_HEIGHT,
        bottom: 0,
      }}
    >
      <Stack
        gap={1.2}
        sx={{
          paddingX: "2rem",
          paddingTop: "2rem",
          backgroundColor: "white",
        }}
      >
        <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"}>
          <Typography textColor={"#334155"} fontWeight={600} fontSize={"18px"}>
            Invoices ({invoices.length || 0})
          </Typography>
          <Stack direction={"row"}>
            <Tooltip title="Dashboard View">
              <Button
                color="neutral"
                variant="plain"
                onClick={() => navigate(`${ADMIN_ROUTE_PREFIX}/finance/invoices-qa/interface-dashboard`)}
              >
                <DashboardIcon />
              </Button>
            </Tooltip>
            <Tooltip title="Refresh">
              <Button
                loading={invoicesQuery.isRefetching}
                disabled={invoicesQuery.isRefetching}
                color="neutral"
                variant="plain"
                onClick={() => invoicesQuery.refetch()}
              >
                <RefreshIcon />
              </Button>
            </Tooltip>
            <InvoicesSummaryByDate />
            <InvoicesSort sortedField={sortedField} setSortedField={setSortedField} />
          </Stack>
        </Stack>
        <DebounceSearchInput placeholder="Search" showClear value={searchText} setValue={setSearchText} />
        <Stack direction="row" gap={1}>
          <MultiSelectFilterBox
            options={productCodeOptions}
            value={productCodes}
            setValue={setProductCodes}
            maxHeight="400px"
            placeholder="Product"
          />
          <MultiSelectFilterBox
            options={[
              { value: "", label: "All" },
              ...invoiceDistinctDates.map((date) => ({ value: date, label: date })),
            ]}
            value={selectedDates}
            setValue={setSelectedDates}
            maxHeight="400px"
            placeholder="Date"
          />
          <MultiSelectFilterBox
            options={[
              { value: "", label: "All" },
              ...INTERFACE_TEAM_MEMBERS.map((member) => ({ value: member.email, label: member.name })),
            ]}
            value={assignees}
            setValue={setAssignees}
            placeholder="Assignee"
          />
        </Stack>
        <Tabs
          aria-keyshortcuts={"disabled"}
          orientation="horizontal"
          color="primary"
          value={activeTab}
          onChange={(_, val) => setActiveTab(val as string)}
        >
          <TabList
            sx={{
              justifyContent: "center",
            }}
          >
            {Object.keys(tabs).map((tabId) => (
              <Tab
                key={(tabs as any)[tabId].id}
                value={(tabs as any)[tabId].id}
                color={activeTab === (tabs as any)[tabId].id ? (tabs as any)[tabId].color ?? "primary" : undefined}
                sx={{
                  py: 0.8,
                  borderRadius: "8px 8px 0 0",
                  px: 2,
                  "&.Mui-selected": {
                    backgroundColor: "transparent !important",
                  },
                }}
              >
                <Typography
                  fontSize={"sm"}
                  color={activeTab === (tabs as any)[tabId].id ? (tabs as any)[tabId].color ?? "primary" : undefined}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {(tabs as any)[tabId].accessName(tabCounts[tabId] ?? 0)}
                </Typography>
              </Tab>
            ))}
          </TabList>
        </Tabs>
      </Stack>
      {invoicesQuery.isLoading ||
      invoicesQuery.isFetching ||
      isLoading ||
      (filteredSortedAssignees.length > 0 && crmInterfaceInvoicesQuery.isFetching) ||
      (activeTab === tabs.parked.id && parkedInvoicesQuery.isLoading) ? (
        <Loader />
      ) : invoicesQuery.error ? (
        <ErrorFallback retry={invoicesQuery.refetch} text="Failed to fetch invoices" />
      ) : invoices.length > 0 ? (
        <Stack sx={{ flex: 1, overflowY: "auto", paddingX: "2rem", paddingY: "1rem" }}>
          <InvoicesList
            selectedInvoice={selectedInvoice}
            setSelectedInvoiceIndex={setSelectedInvoiceIndex}
            searchText={searchText}
            selectedInvoiceIndex={selectedInvoiceIndex}
            invoices={invoices}
          />
        </Stack>
      ) : (
        <NotFoundFallback size="sm" text="No invoices found" />
      )}
    </Stack>
  );
}
